// WhiptSummPanel - Document Summary & Q&A with database storage
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Upload, Loader2, Sparkles, Send, Copy, Download, Trash2, FileText, Maximize2, Minimize2, Search, Edit2, Check } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createFlexibleRepository } from '@/repositories/flexibleEntityRepository';
import { useAuth } from '@/contexts/AuthContext';
import { extractDocumentText } from '@/lib/documentExtractor';
import { generateSummary } from '@/lib/summaryGenerator';

// Custom Quill editor styles - Word document style
const quillStyles = `
  .quill-editor-readonly .ql-toolbar {
    display: none;
  }
  
  .quill-editor-readonly .ql-container {
    border: none;
  }
  
  .quill-editor-editable .ql-toolbar {
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: 0.5rem 0.5rem 0 0;
  }
  
  .quill-editor-editable .ql-container {
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-top: none;
    border-radius: 0 0 0.5rem 0.5rem;
  }
  
  .word-doc-style .ql-container {
    background: white;
    padding: 2rem 3rem;
    min-height: 600px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  .word-doc-style .ql-editor {
    color: #000000;
    font-family: 'Times New Roman', Times, serif;
    font-size: 12pt;
    line-height: 2;
    min-height: 600px;
  }
  
  .word-doc-style .ql-editor h1 {
    color: #000000;
    font-weight: bold;
    font-size: 24pt;
    margin-top: 0;
    margin-bottom: 12pt;
  }
  
  .word-doc-style .ql-editor h2 {
    color: #000000;
    font-weight: bold;
    font-size: 18pt;
    margin-top: 12pt;
    margin-bottom: 6pt;
  }
  
  .word-doc-style .ql-editor h3 {
    color: #000000;
    font-weight: bold;
    font-size: 14pt;
    margin-top: 10pt;
    margin-bottom: 4pt;
  }
  
  .word-doc-style .ql-editor p {
    margin-bottom: 8pt;
  }
  
  .word-doc-style .ql-editor strong {
    color: #000000;
    font-weight: bold;
  }
  
  .word-doc-style .ql-editor ul,
  .word-doc-style .ql-editor ol {
    padding-left: 1.5em;
  }
  
  .ql-snow .ql-stroke {
    stroke: rgba(251, 191, 36, 0.8);
  }
  
  .ql-snow .ql-fill {
    fill: rgba(251, 191, 36, 0.8);
  }
  
  .ql-snow .ql-picker-label {
    color: rgba(251, 191, 36, 0.8);
  }
  
  .ql-snow .ql-toolbar button:hover,
  .ql-snow .ql-toolbar button.ql-active {
    color: rgb(251, 191, 36);
  }
  
  .ql-snow .ql-toolbar button:hover .ql-stroke,
  .ql-snow .ql-toolbar button.ql-active .ql-stroke {
    stroke: rgb(251, 191, 36);
  }
  
  .ql-snow .ql-toolbar button:hover .ql-fill,
  .ql-snow .ql-toolbar button.ql-active .ql-fill {
    fill: rgb(251, 191, 36);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = quillStyles;
  document.head.appendChild(styleElement);
}

interface WhiptSummPanelProps {
  onClose?: () => void;
  embedded?: boolean;
}

interface QAPair {
  question: string;
  answer: string;
}

interface SavedSummary {
  id: number;
  title: string;
  documentText: string;
  summary: string;
  fileName: string;
  fileType: string;
  wordCount: number;
  qaPairs?: QAPair[];
  createdAt: string;
}

const WhiptSummPanel: React.FC<WhiptSummPanelProps> = ({ onClose, embedded = false }) => {
  const { user } = useAuth();
  const summaryRepo = useMemo(() => createFlexibleRepository({ entityType: 'DocumentSummary', orderBy: 'updated_at DESC' }), []);

  // State
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentExtracted, setDocumentExtracted] = useState<{
    text: string;
    type: 'pdf' | 'docx' | 'txt';
    fileName: string;
    wordCount: number;
  } | null>(null);
  const [summary, setSummary] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState<'qa' | 'notes' | 'flashcards'>('qa');

  // Q&A State
  const [showQA, setShowQA] = useState(false);
  const [qaPairs, setQAPairs] = useState<QAPair[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Database State
  const [savedSummaries, setSavedSummaries] = useState<SavedSummary[]>([]);
  const [currentSummaryId, setCurrentSummaryId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'idle'>('idle');
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editedTitle, setEditedTitle] = useState('');

  // Auto-save timer ref
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load saved summaries
  useEffect(() => {
    loadSavedSummaries();
  }, [user]);

  // Auto-save effect - triggers when summary or qaPairs change
  useEffect(() => {
    if (!documentExtracted || !summary || !user?.id) return;
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set status to saving after 2 seconds of inactivity
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveSummary();
    }, 2000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [summary, qaPairs]);

  const autoSaveSummary = async () => {
    if (!user?.id || !documentExtracted || !summary) return;
    
    setAutoSaveStatus('saving');
    try {
      const summaryData = {
        user_id: user.id,  // CRITICAL: Must include user_id in data for filtering
        title: documentExtracted.fileName.replace(/\.[^/.]+$/, ''),
        documentText: documentExtracted.text,
        summary,
        fileName: documentExtracted.fileName,
        fileType: documentExtracted.type,
        wordCount: documentExtracted.wordCount,
        qaPairs,
      };

      console.log('Auto-saving summary:', summaryData);

      if (currentSummaryId) {
        console.log('Updating existing summary:', currentSummaryId);
        await summaryRepo.update(currentSummaryId, summaryData);
      } else {
        console.log('Creating new summary');
        const newItem = await summaryRepo.create(summaryData);
        console.log('Created summary with ID:', newItem.id);
        setCurrentSummaryId(Number(newItem.id));
      }

      setAutoSaveStatus('saved');
      await loadSavedSummaries();
      
      // Reset to idle after 2 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('idle');
    }
  };

  const loadSavedSummaries = async () => {
    if (!user?.id) return;
    try {
      console.log('Loading summaries for user:', user.id);
      const items = await summaryRepo.list(user.id);
      console.log('Loaded items:', items);
      setSavedSummaries(items.map((item: any) => ({
        id: item.id,
        title: item.data?.title || 'Untitled Summary',
        documentText: item.data?.documentText || '',
        summary: item.data?.summary || '',
        fileName: item.data?.fileName || '',
        fileType: item.data?.fileType || '',
        wordCount: item.data?.wordCount || 0,
        qaPairs: item.data?.qaPairs || [],
        createdAt: item.created_at,
      })));
    } catch (error) {
      console.error('Error loading summaries:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    setIsProcessing(true);
    setDocumentFile(file);
    
    try {
      const extracted = await extractDocumentText(file);
      setDocumentExtracted(extracted);
      
      // Auto-generate summary with HTML formatting
      setSummaryLoading(true);
      const generatedSummary = await generateSummary(extracted.text, '');
      
      // Format the summary with proper HTML structure
      const formattedSummary = formatSummaryAsHTML(generatedSummary);
      setSummary(formattedSummary);
      setShowSummary(true);
      setSummaryLoading(false);
      
    } catch (error) {
      console.error('Error processing document:', error);
      alert('Error processing document. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format plain text summary as HTML with headings
  const formatSummaryAsHTML = (text: string): string => {
    let html = text;
    
    // Format academic headers: "Background:", "Objectives:", "Methods:", "Results:", "Conclusions:"
    // Make them bold inline labels (not separate headers)
    html = html.replace(/^(Background|Objectives|Methods|Results|Conclusions):\s*/gim, '<p><strong>$1:</strong> ');
    
    // Close paragraphs that were opened by the above replacement
    html = html.replace(/(<p><strong>(?:Background|Objectives|Methods|Results|Conclusions):<\/strong> [^<]+)/gi, '$1</p>');
    
    // Split by double newlines to handle paragraph breaks
    const paragraphs = html.split(/\n\n+/);
    
    html = paragraphs.map(para => {
      para = para.trim();
      if (!para) return '';
      
      // If already wrapped in tags, return as-is
      if (para.startsWith('<p>') || para.startsWith('<h')) {
        return para;
      }
      
      // Otherwise wrap in paragraph tags
      return `<p>${para}</p>`;
    }).filter(p => p).join('\n\n');
    
    return html;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type.includes('pdf') || file.type.includes('word') || file.type.includes('text'))) {
      handleFileUpload(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  // Save summary to database
  const saveSummary = async () => {
    if (!user?.id || !documentExtracted || !summary) return;
    
    setIsSaving(true);
    try {
      const summaryData = {
        user_id: user.id,  // CRITICAL: Must include user_id in data for filtering
        title: documentExtracted.fileName.replace(/\.[^/.]+$/, ''),
        documentText: documentExtracted.text,
        summary,
        fileName: documentExtracted.fileName,
        fileType: documentExtracted.type,
        wordCount: documentExtracted.wordCount,
        qaPairs,
      };

      if (currentSummaryId) {
        await summaryRepo.update(currentSummaryId, summaryData);
      } else {
        const newItem = await summaryRepo.create(summaryData);
        setCurrentSummaryId(Number(newItem.id));
      }

      await loadSavedSummaries();
    } catch (error) {
      console.error('Error saving summary:', error);
      alert('Error saving summary. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Generate Q&A answer
  const generateAnswer = async (question: string) => {
    if (!documentExtracted?.text) return;
    
    setIsGenerating(true);
    try {
      // Use secure Edge Function instead of direct OpenAI call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please sign in to use AI features');
      }

      const response = await supabase.functions.invoke('whipt-ai-summary', {
        body: {
          text: `Document:\n${documentExtracted.text.slice(0, 4000)}\n\nQuestion: ${question}`,
          prompt: 'You are a helpful assistant that answers questions about documents. Provide clear, concise answers based on the document content.',
          model: 'gpt-4o-mini',
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const answer = response.data?.summary || 'Unable to generate answer';

      
      setQAPairs([...qaPairs, { question, answer }]);
      setCurrentQuestion('');
    } catch (error) {
      console.error('Error generating answer:', error);
      alert('Error generating answer. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Load saved summary
  const loadSummary = (saved: SavedSummary) => {
    setCurrentSummaryId(saved.id);
    setDocumentExtracted({
      text: saved.documentText,
      type: saved.fileType as 'pdf' | 'docx' | 'txt',
      fileName: saved.fileName,
      wordCount: saved.wordCount,
    });
    setSummary(saved.summary);
    setQAPairs(saved.qaPairs || []);
    setShowSummary(true);
    setShowSidebar(false);
  };

  // Delete summary
  const deleteSummary = async (id: number) => {
    if (!confirm('Delete this summary?')) return;
    try {
      await summaryRepo.remove(id);
      await loadSavedSummaries();
      if (currentSummaryId === id) {
        resetState();
      }
    } catch (error) {
      console.error('Error deleting summary:', error);
    }
  };

  // Edit summary title
  const startEditingTitle = (summary: SavedSummary) => {
    setEditingTitleId(summary.id);
    setEditedTitle(summary.title);
  };

  const saveTitle = async (id: number) => {
    if (!editedTitle.trim()) return;
    
    try {
      const summary = savedSummaries.find(s => s.id === id);
      if (!summary) return;
      
      await summaryRepo.update(id, {
        ...summary,
        title: editedTitle.trim(),
      });
      
      await loadSavedSummaries();
      setEditingTitleId(null);
      setEditedTitle('');
      
      // Update current summary title if it's the one being edited
      if (currentSummaryId === id && documentExtracted) {
        // Title is just for display, doesn't affect the editor
      }
    } catch (error) {
      console.error('Error updating title:', error);
    }
  };

  const cancelEditTitle = () => {
    setEditingTitleId(null);
    setEditedTitle('');
  };

  const resetState = () => {
    setDocumentFile(null);
    setDocumentExtracted(null);
    setSummary('');
    setQAPairs([]);
    setCurrentSummaryId(null);
    setShowSummary(false);
    setShowQA(false);
  };

  const filteredSummaries = savedSummaries.filter(s =>
    s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.fileName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-amber-400/30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="lg:hidden text-amber-300 hover:text-amber-100 p-2 hover:bg-amber-400/10 rounded-lg transition-colors"
          >
            <Search className="w-5 h-5" />
          </button>
          <span className="text-3xl font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">WHIPT</span>
          <span className="text-2xl font-bold text-amber-100">SUMM!</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-amber-300 hover:text-amber-100 transition-colors p-2 hover:bg-amber-400/10 rounded-lg"
          >
            {isMinimized ? <Maximize2 className="w-5 h-5" /> : <Minimize2 className="w-5 h-5" />}
          </button>
          <button
            onClick={onClose}
            className="text-amber-300 hover:text-amber-100 transition-colors p-2 hover:bg-amber-400/10 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Saved Summaries */}
        <div className="w-80 border-r border-amber-400/30 flex flex-col bg-black/20">
          <div className="p-4 border-b border-amber-400/30">
            <h3 className="font-bold text-amber-100 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Saved Summaries
            </h3>
            <input
              type="text"
              placeholder="Search summaries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 bg-amber-400/10 border border-amber-400/30 rounded-lg text-amber-100 placeholder-amber-300/50 focus:outline-none focus:border-amber-400 text-sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {filteredSummaries.length === 0 ? (
              <p className="text-amber-200/60 text-sm text-center py-8">No saved summaries yet</p>
            ) : (
              filteredSummaries.map((saved) => (
                <div
                  key={saved.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    currentSummaryId === saved.id
                      ? 'bg-amber-400/20 border-amber-400'
                      : 'bg-amber-400/5 border-amber-400/20 hover:bg-amber-400/10'
                  }`}
                  onClick={() => editingTitleId !== saved.id && loadSummary(saved)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {editingTitleId === saved.id ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') saveTitle(saved.id);
                              if (e.key === 'Escape') cancelEditTitle();
                            }}
                            className="flex-1 px-2 py-1 bg-amber-400/20 border border-amber-400 rounded text-amber-100 text-sm focus:outline-none"
                            autoFocus
                          />
                          <button
                            onClick={() => saveTitle(saved.id)}
                            className="text-green-400 hover:text-green-300 p-1"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditTitle}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <h3 className="font-semibold text-amber-100 truncate text-sm">{saved.title}</h3>
                      )}
                      <p className="text-xs text-amber-200/60 truncate">{saved.fileName}</p>
                      <p className="text-xs text-amber-200/50 mt-1">
                        {new Date(saved.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    {editingTitleId !== saved.id && (
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingTitle(saved);
                          }}
                          className="text-amber-400 hover:text-amber-300 p-1"
                          title="Edit name"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSummary(saved.id);
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Middle Column - Summary Editor */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 border-r border-amber-400/30">
          {!showSummary ? (
            <>
              {/* Upload Area */}
              <div className="text-center space-y-4">
                <div className="text-6xl">📝</div>
                <h2 className="text-2xl font-bold text-amber-100">Upload Your Document</h2>
                <p className="text-amber-200/70">Get instant AI-powered summaries</p>
              </div>

              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${
                  isDragging
                    ? 'border-amber-400 bg-amber-400/20'
                    : 'border-amber-400/30 bg-amber-400/5 hover:border-amber-400/50'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
              >
                <input
                  type="file"
                  className="hidden"
                  id="whipt-summ-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileInput}
                  disabled={isProcessing}
                />
                <label htmlFor="whipt-summ-upload" className="cursor-pointer">
                  {isProcessing ? (
                    <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-amber-400" />
                  ) : (
                    <FileText className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                  )}
                  <p className="text-amber-100 font-semibold mb-2">
                    {isProcessing ? 'Processing...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-amber-200/60 text-sm">PDF, DOC, DOCX, TXT (Max 10MB)</p>
                </label>
              </div>

              <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl p-4 space-y-2">
                <h3 className="font-bold text-amber-100 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span>What WHIPT SUMM! does:</span>
                </h3>
                <ul className="space-y-2 text-amber-200/80 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    <span>Extracts key points and main ideas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    <span>Generates concise summaries in seconds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    <span>Interactive Q&A about your document</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    <span>Save and organize all your summaries</span>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              {/* Document Info */}
              <div className="bg-amber-400/10 border border-amber-400/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-amber-100">{documentExtracted?.fileName}</h3>
                    <p className="text-sm text-amber-200/60">{documentExtracted?.wordCount.toLocaleString()} words</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Auto-save status */}
                    {autoSaveStatus !== 'idle' && (
                      <div className="flex items-center gap-2 text-sm">
                        {autoSaveStatus === 'saving' && (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                            <span className="text-amber-300">Saving...</span>
                          </>
                        )}
                        {autoSaveStatus === 'saved' && (
                          <>
                            <span className="text-green-400">✓</span>
                            <span className="text-green-400">Saved</span>
                          </>
                        )}
                      </div>
                    )}
                    <button
                      onClick={saveSummary}
                      disabled={isSaving}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 text-sm"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Now'}
                    </button>
                    <button
                      onClick={resetState}
                      className="px-4 py-2 bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 rounded-lg font-semibold transition-colors text-sm"
                    >
                      New
                    </button>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-amber-400/5 border border-amber-400/30 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-amber-100 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Summary
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = summary;
                        navigator.clipboard.writeText(tempDiv.textContent || summary);
                      }}
                      className="text-amber-300 hover:text-amber-100 p-2 hover:bg-amber-400/10 rounded-lg transition-colors"
                      title="Copy as plain text"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setIsEditingSummary(!isEditingSummary)}
                      className={`p-2 rounded-lg transition-colors ${
                        isEditingSummary
                          ? 'bg-amber-500 text-white'
                          : 'text-amber-300 hover:text-amber-100 hover:bg-amber-400/10'
                      }`}
                      title={isEditingSummary ? 'View mode' : 'Edit mode'}
                    >
                      {isEditingSummary ? <Sparkles className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {summaryLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                  </div>
                ) : (
                  <div className="word-doc-style">
                    <ReactQuill
                      value={summary}
                      onChange={setSummary}
                      readOnly={!isEditingSummary}
                      theme="snow"
                      modules={{
                        toolbar: isEditingSummary ? [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'align': [] }],
                          ['clean']
                        ] : false
                      }}
                      className={isEditingSummary ? 'quill-editor-editable' : 'quill-editor-readonly'}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right Column - Q&A */}
        <div className="w-96 overflow-y-auto p-6 space-y-6 bg-black/20">
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-amber-100 flex items-center gap-2">
              <Send className="w-5 h-5" />
              Questions & Answers
            </h3>

            {!showSummary ? (
              <div className="text-center py-12 text-amber-200/60 text-sm">
                <p>Upload a document to start asking questions</p>
              </div>
            ) : (
              <>
                {/* Q&A Pairs */}
                <div className="space-y-4">
                  {qaPairs.length === 0 ? (
                    <div className="text-center py-8 text-amber-200/60 text-sm">
                      <p>No questions yet. Ask something about your document!</p>
                    </div>
                  ) : (
                    qaPairs.map((qa, index) => (
                      <div key={index} className="bg-amber-400/10 rounded-lg p-4 border border-amber-400/20">
                        <p className="font-semibold text-amber-100 mb-2 text-sm">Q: {qa.question}</p>
                        <p className="text-amber-200/80 text-sm">A: {qa.answer}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Question Input */}
                <div className="space-y-2">
                  <input
                    type="text"
                    value={currentQuestion}
                    onChange={(e) => setCurrentQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !isGenerating && generateAnswer(currentQuestion)}
                    placeholder="Ask a question about this document..."
                    className="w-full px-4 py-3 bg-amber-400/10 border border-amber-400/30 rounded-lg text-amber-100 placeholder-amber-300/50 focus:outline-none focus:border-amber-400 text-sm"
                    disabled={isGenerating}
                  />
                  <button
                    onClick={() => generateAnswer(currentQuestion)}
                    disabled={!currentQuestion || isGenerating}
                    className="w-full px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Ask Question
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WhiptSummPanel;
